import type { DashboardNarrativeContent } from "src/components/common";
import type { DashboardNarrativeSegment } from "src/services/DashboardNarrativeAPI";
import { BRANCHES, MENU_ITEMS } from "../data/restaurantDemoData";
import type { RestaurantFilters, RestaurantMetrics } from "./restaurantMetrics";

export type RestaurantSummaryTab =
  | "overview"
  | "sales-orders"
  | "branches"
  | "menu"
  | "channels-customers";

interface SummaryFormatters {
  // eslint-disable-next-line no-unused-vars
  formatCurrency: (value: number, options?: { compact?: boolean }) => string;
  /** Currency with two decimals, for averages such as AOV. */
  // eslint-disable-next-line no-unused-vars
  formatCurrencyAverage: (value: number) => string;
  // eslint-disable-next-line no-unused-vars
  formatNumber: (value: number, decimals?: number) => string;
  // eslint-disable-next-line no-unused-vars
  formatPercent: (value: number, decimals?: number) => string;
  // eslint-disable-next-line no-unused-vars
  formatMinutes: (value: number, decimals?: number) => string;
  // eslint-disable-next-line no-unused-vars
  formatDate: (isoDate: string) => string;
  isRTL: boolean;
}

interface BuildSummaryParams {
  tab: RestaurantSummaryTab;
  metrics: RestaurantMetrics;
  filters: RestaurantFilters;
  fmt: SummaryFormatters;
}

const branchName = (id: string, isRTL: boolean) => {
  const branch = BRANCHES.find(b => b.id === id);
  if (!branch) {return id;}
  return isRTL ? branch.nameAr : branch.name;
};

const menuItemName = (id: string, isRTL: boolean) => {
  const item = MENU_ITEMS.find(m => m.id === id);
  if (!item) {return id;}
  return isRTL ? item.nameAr : item.name;
};

/** [English, Arabic] copy for the enum ids the narrative interpolates into sentences. */
const localizedLabel = (labels: Record<string, [string, string]>) => (id: string, isRTL: boolean) => {
  const pair = labels[id];
  if (!pair) {return id.replace(/_/g, " ");}
  return isRTL ? pair[1] : pair[0];
};

const daypartLabel = localizedLabel({
  breakfast: ["Breakfast", "الإفطار"],
  lunch: ["Lunch", "الغداء"],
  afternoon: ["Afternoon", "العصر"],
  dinner: ["Dinner", "العشاء"],
  late_night: ["Late night", "منتصف الليل"],
});

const orderTypeLabel = localizedLabel({
  dine_in: ["Dine-in", "تناول في المطعم"],
  takeaway: ["Takeaway", "طلبات خارجية"],
  delivery: ["Delivery", "التوصيل"],
  drive_thru: ["Drive-thru", "السيارة"],
  pickup: ["Pickup", "الاستلام"],
});

const channelLabel = localizedLabel({
  mobile_app: ["Mobile app", "تطبيق الجوال"],
  website: ["Website", "الموقع الإلكتروني"],
  kiosk: ["Kiosk", "الكشك الذاتي"],
  pos: ["POS", "نقطة البيع"],
  call_center: ["Call center", "مركز الاتصال"],
  aggregator: ["Aggregator", "منصات التوصيل"],
});

const categoryLabel = localizedLabel({
  grills: ["Grills", "المشاوي"],
  rice_dishes: ["Rice dishes", "أطباق الأرز"],
  sandwiches: ["Sandwiches", "الساندويتشات"],
  appetizers: ["Appetizers", "المقبلات"],
  beverages: ["Beverages", "المشروبات"],
  desserts: ["Desserts", "الحلويات"],
});

/**
 * Wraps a formatted figure in `++...++` / `--...--` so `DashboardNarrativePanel` paints it
 * green or red, driven by that metric's own delta rather than the sentence's tone.
 * `direction: "inverse"` is for metrics where a rise is bad — cancellations, refunds,
 * fulfilment time, abandonment. Movements under the `movement()` "broadly stable"
 * threshold stay uncoloured so the copy and the colour never disagree.
 */
const STABLE_DELTA_THRESHOLD = 2;

const colorize = (
  formattedValue: string,
  delta: number | null | undefined,
  direction: "normal" | "inverse" = "normal",
): string => {
  if (delta === null || delta === undefined || Math.abs(delta) < STABLE_DELTA_THRESHOLD) {
    return formattedValue;
  }
  const improving = direction === "inverse" ? delta < 0 : delta > 0;
  return improving ? `++${formattedValue}++` : `--${formattedValue}--`;
};

function movement(delta: number, isRTL: boolean): string {
  const abs = Math.abs(delta);
  const direction = delta >= 0
    ? (isRTL ? "ارتفعت" : "increased")
    : (isRTL ? "انخفضت" : "decreased");
  const stable = isRTL ? "ظلت مستقرة نسبياً" : "remained broadly stable";

  if (abs < 2) {return stable;}
  if (abs < 5) {return isRTL ? `${direction} قليلاً` : `${direction} slightly`;}
  if (abs <= 15) {return direction;}
  return isRTL ? `${direction} بشكل ملحوظ` : `${direction} significantly`;
}

function buildOverviewInsights(metrics: RestaurantMetrics, filters: RestaurantFilters, fmt: SummaryFormatters): string[] {
  const { isRTL } = fmt;
  const insights: string[] = [];

  insights.push(
    isRTL
      ? `بلغت الإيرادات ${colorize(fmt.formatCurrency(metrics.revenue, { compact: true }), metrics.revenueDelta)} حيث ${movement(metrics.revenueDelta, isRTL)} مقارنة بالفترة السابقة.`
      : `Revenue reached ${colorize(fmt.formatCurrency(metrics.revenue, { compact: true }), metrics.revenueDelta)}, having ${movement(metrics.revenueDelta, isRTL)} versus the prior period.`,
  );

  insights.push(
    isRTL
      ? `متوسط قيمة الطلب ${movement(metrics.aovDelta, isRTL)} إلى ${colorize(fmt.formatCurrencyAverage(metrics.aov), metrics.aovDelta)} عبر ${colorize(fmt.formatNumber(metrics.orders), metrics.ordersDelta)} طلب.`
      : `Average order value ${movement(metrics.aovDelta, isRTL)} to ${colorize(fmt.formatCurrencyAverage(metrics.aov), metrics.aovDelta)} across ${colorize(fmt.formatNumber(metrics.orders), metrics.ordersDelta)} orders.`,
  );

  const topType = [...metrics.orderTypes].sort((a, b) => b.share - a.share)[0];
  if (topType) {
    insights.push(
      isRTL
        ? `يمثل ${orderTypeLabel(topType.id, isRTL)} أعلى نسبة من الطلبات بحصة ${fmt.formatPercent(topType.share)}.`
        : `${orderTypeLabel(topType.id, isRTL)} leads order mix with a ${fmt.formatPercent(topType.share)} share.`,
    );
  }

  if (filters.branchId === "all" && metrics.branches.length > 1) {
    const strongest = metrics.branches[0];
    const weakest = metrics.branches[metrics.branches.length - 1];
    insights.push(
      isRTL
        ? `${branchName(strongest.id, isRTL)} هو الفرع الأقوى بينما يحتاج ${branchName(weakest.id, isRTL)} إلى اهتمام أكبر.`
        : `${branchName(strongest.id, isRTL)} is the strongest branch, while ${branchName(weakest.id, isRTL)} trails the network.`,
    );
  }

  const busiestDaypart = [...metrics.dayparts].sort((a, b) => b.orders - a.orders)[0];
  if (busiestDaypart) {
    insights.push(
      isRTL
        ? `فترة ${daypartLabel(busiestDaypart.id, isRTL)} هي الأكثر ازدحاماً، وتبلغ ذروتها خلال عطلة نهاية الأسبوع.`
        : `${daypartLabel(busiestDaypart.id, isRTL)} is the busiest daypart, peaking over the weekend.`,
    );
  }

  const topItemByRevenue = metrics.menu[0];
  const topItemByUnits = [...metrics.menu].sort((a, b) => b.units - a.units)[0];
  if (topItemByRevenue && topItemByUnits) {
    insights.push(
      isRTL
        ? `${menuItemName(topItemByRevenue.id, isRTL)} يتصدر الإيرادات بينما ${menuItemName(topItemByUnits.id, isRTL)} يتصدر عدد الوحدات المباعة.`
        : `${menuItemName(topItemByRevenue.id, isRTL)} leads by revenue, while ${menuItemName(topItemByUnits.id, isRTL)} leads by units sold.`,
    );
  }

  insights.push(
    isRTL
      ? `عدد الطلبات ${movement(metrics.ordersDelta, isRTL)} إلى ${colorize(fmt.formatNumber(metrics.orders), metrics.ordersDelta)} طلب عبر ${fmt.formatNumber(metrics.activeBranches)} فرع نشط.`
      : `Order volume ${movement(metrics.ordersDelta, isRTL)} to ${colorize(fmt.formatNumber(metrics.orders), metrics.ordersDelta)} orders across ${fmt.formatNumber(metrics.activeBranches)} active branches.`,
  );

  const cancelRateOverview = metrics.orders > 0 ? metrics.cancelledOrders / metrics.orders : 0;
  insights.push(
    isRTL
      ? `متوسط وقت التنفيذ ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")} وقد ${movement(metrics.fulfilmentMinutesDelta, isRTL)}، بمعدل إلغاء ${colorize(fmt.formatPercent(cancelRateOverview), metrics.cancelledOrdersDelta, "inverse")}.`
      : `Average fulfilment time is ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")} and has ${movement(metrics.fulfilmentMinutesDelta, isRTL)}, with a ${colorize(fmt.formatPercent(cancelRateOverview), metrics.cancelledOrdersDelta, "inverse")} cancellation rate.`,
  );

  insights.push(
    isRTL
      ? `معدل تكرار الطلب ${colorize(fmt.formatPercent(metrics.repeatRate), metrics.repeatRateDelta)} وقد ${movement(metrics.repeatRateDelta, isRTL)} مقارنة بالفترة السابقة.`
      : `Repeat order rate stands at ${colorize(fmt.formatPercent(metrics.repeatRate), metrics.repeatRateDelta)}, having ${movement(metrics.repeatRateDelta, isRTL)} versus the prior period.`,
  );

  return insights.slice(0, 8);
}

function buildSalesOrdersInsights(metrics: RestaurantMetrics, fmt: SummaryFormatters): string[] {
  const { isRTL } = fmt;
  const insights: string[] = [];
  const cancelRate = metrics.orders > 0 ? metrics.cancelledOrders / metrics.orders : 0;
  const discountShare = metrics.revenue > 0 ? metrics.discountAmount / metrics.revenue : 0;

  insights.push(
    isRTL
      ? `حققت الإيرادات ${colorize(fmt.formatCurrency(metrics.revenue, { compact: true }), metrics.revenueDelta)} خلال الفترة المحددة.`
      : `Revenue totalled ${colorize(fmt.formatCurrency(metrics.revenue, { compact: true }), metrics.revenueDelta)} for the selected period.`,
  );

  const byAov = [...metrics.orderTypes].sort((a, b) => b.aov - a.aov)[0];
  const bySmallest = [...metrics.orderTypes].sort((a, b) => a.share - b.share)[0];
  if (byAov) {
    insights.push(
      isRTL
        ? `${orderTypeLabel(byAov.id, isRTL)} يحقق أعلى متوسط لقيمة الطلب عند ${fmt.formatCurrencyAverage(byAov.aov)}.`
        : `${orderTypeLabel(byAov.id, isRTL)} carries the highest average order value at ${fmt.formatCurrencyAverage(byAov.aov)}.`,
    );
  }
  if (bySmallest) {
    insights.push(
      isRTL
        ? `${orderTypeLabel(bySmallest.id, isRTL)} يمثل أصغر حصة من مزيج الطلبات.`
        : `${orderTypeLabel(bySmallest.id, isRTL)} represents the smallest share of the order mix.`,
    );
  }

  insights.push(
    isRTL
      ? `الخصومات تمثل ${colorize(fmt.formatPercent(discountShare), metrics.discountAmountDelta, "inverse")} من الإيرادات، بمعدل إلغاء ${colorize(fmt.formatPercent(cancelRate), metrics.cancelledOrdersDelta, "inverse")}.`
      : `Discounts account for ${colorize(fmt.formatPercent(discountShare), metrics.discountAmountDelta, "inverse")} of revenue, with a ${colorize(fmt.formatPercent(cancelRate), metrics.cancelledOrdersDelta, "inverse")} cancellation rate.`,
  );

  const strongestDay = [...metrics.daily].sort((a, b) => b.revenue - a.revenue)[0];
  if (strongestDay) {
    insights.push(
      isRTL
        ? `أقوى يوم كان ${fmt.formatDate(strongestDay.date)} بإيرادات ${fmt.formatCurrency(strongestDay.revenue, { compact: true })}.`
        : `The strongest single day was ${fmt.formatDate(strongestDay.date)}, at ${fmt.formatCurrency(strongestDay.revenue, { compact: true })}.`,
    );
  }

  insights.push(
    isRTL
      ? `عدد الطلبات ${movement(metrics.ordersDelta, isRTL)} إلى ${colorize(fmt.formatNumber(metrics.orders), metrics.ordersDelta)} طلب، بمتوسط ${colorize(fmt.formatNumber(metrics.itemsPerOrder, 1), metrics.itemsPerOrderDelta)} عنصر لكل طلب.`
      : `Order volume ${movement(metrics.ordersDelta, isRTL)} to ${colorize(fmt.formatNumber(metrics.orders), metrics.ordersDelta)} orders, averaging ${colorize(fmt.formatNumber(metrics.itemsPerOrder, 1), metrics.itemsPerOrderDelta)} items per order.`,
  );

  insights.push(
    isRTL
      ? `قيمة المبالغ المستردة ${colorize(fmt.formatCurrency(metrics.refundedAmount, { compact: true }), metrics.refundedAmountDelta, "inverse")}، وقد ${movement(metrics.refundedAmountDelta, isRTL)} مقارنة بالفترة السابقة.`
      : `Refunded amounts total ${colorize(fmt.formatCurrency(metrics.refundedAmount, { compact: true }), metrics.refundedAmountDelta, "inverse")}, having ${movement(metrics.refundedAmountDelta, isRTL)} versus the prior period.`,
  );

  const weekdayTotals = new Map<number, { revenue: number; orders: number }>();
  metrics.daily.forEach(point => {
    const weekday = new Date(`${point.date}T00:00:00Z`).getUTCDay();
    const bucket = weekdayTotals.get(weekday) ?? { revenue: 0, orders: 0 };
    bucket.revenue += point.revenue;
    bucket.orders += point.orders;
    weekdayTotals.set(weekday, bucket);
  });
  const weekdayNames = isRTL
    ? ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"]
    : ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const bestWeekday = Array.from(weekdayTotals.entries()).sort((a, b) => b[1].revenue - a[1].revenue)[0];
  if (bestWeekday && metrics.daily.length >= 7) {
    insights.push(
      isRTL
        ? `يوم ${weekdayNames[bestWeekday[0]]} هو الأعلى إيراداً على مدار الأسبوع.`
        : `${weekdayNames[bestWeekday[0]]} is the highest-revenue day of the week.`,
    );
  }

  return insights.slice(0, 8);
}

function buildBranchesInsights(metrics: RestaurantMetrics, filters: RestaurantFilters, fmt: SummaryFormatters): string[] {
  const { isRTL } = fmt;
  const insights: string[] = [];

  if (filters.branchId !== "all" && metrics.branches.length === 1) {
    const branch = metrics.branches[0];
    insights.push(
      isRTL
        ? `${branchName(branch.id, isRTL)} حقق إيرادات ${fmt.formatCurrency(branch.revenue, { compact: true })} من ${fmt.formatNumber(branch.orders)} طلب.`
        : `${branchName(branch.id, isRTL)} generated ${fmt.formatCurrency(branch.revenue, { compact: true })} from ${fmt.formatNumber(branch.orders)} orders.`,
    );
    insights.push(
      isRTL
        ? `متوسط قيمة الطلب ${fmt.formatCurrencyAverage(branch.aov)} بمتوسط وقت تنفيذ ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")}.`
        : `Average order value stands at ${fmt.formatCurrencyAverage(branch.aov)}, with an average fulfilment time of ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")}.`,
    );
    insights.push(
      isRTL
        ? `عدد الطلبات ${movement(metrics.ordersDelta, isRTL)} إلى ${fmt.formatNumber(branch.orders)} طلب، بمعدل إلغاء ${colorize(fmt.formatPercent(metrics.orders > 0 ? metrics.cancelledOrders / metrics.orders : 0), metrics.cancelledOrdersDelta, "inverse")}.`
        : `Order volume ${movement(metrics.ordersDelta, isRTL)} to ${fmt.formatNumber(branch.orders)} orders, with a ${colorize(fmt.formatPercent(metrics.orders > 0 ? metrics.cancelledOrders / metrics.orders : 0), metrics.cancelledOrdersDelta, "inverse")} cancellation rate.`,
    );
  } else {
    const leader = metrics.branches[0];
    const top3Share = metrics.branches.slice(0, 3).reduce((sum, b) => sum + b.share, 0);
    const worst = metrics.branches[metrics.branches.length - 1];
    if (leader) {
      insights.push(
        isRTL
          ? `${branchName(leader.id, isRTL)} يقود الشبكة بإيرادات ${fmt.formatCurrency(leader.revenue, { compact: true })}.`
          : `${branchName(leader.id, isRTL)} leads the network with ${fmt.formatCurrency(leader.revenue, { compact: true })} in revenue.`,
      );
    }
    insights.push(
      isRTL
        ? `أعلى 3 فروع تمثل ${fmt.formatPercent(top3Share)} من إجمالي الإيرادات.`
        : `The top 3 branches account for ${fmt.formatPercent(top3Share)} of total revenue.`,
    );
    if (worst) {
      insights.push(
        isRTL
          ? `${branchName(worst.id, isRTL)} يسجل أدنى أداء بين الفروع النشطة بإيرادات ${fmt.formatCurrency(worst.revenue, { compact: true })}.`
          : `${branchName(worst.id, isRTL)} records the lowest performance among active branches, at ${fmt.formatCurrency(worst.revenue, { compact: true })} in revenue.`,
      );
    }

    const byAov = [...metrics.branches].sort((a, b) => b.aov - a.aov)[0];
    const byAovLowest = [...metrics.branches].sort((a, b) => a.aov - b.aov)[0];
    if (byAov && byAovLowest && byAov.id !== byAovLowest.id) {
      insights.push(
        isRTL
          ? `${branchName(byAov.id, isRTL)} يحقق أعلى متوسط لقيمة الطلب (${fmt.formatCurrencyAverage(byAov.aov)})، مقابل ${fmt.formatCurrencyAverage(byAovLowest.aov)} في ${branchName(byAovLowest.id, isRTL)}.`
          : `${branchName(byAov.id, isRTL)} posts the highest average order value (${fmt.formatCurrencyAverage(byAov.aov)}), versus ${fmt.formatCurrencyAverage(byAovLowest.aov)} at ${branchName(byAovLowest.id, isRTL)}.`,
      );
    }
  }

  const busiestDaypart = [...metrics.dayparts].sort((a, b) => b.orders - a.orders)[0];
  if (busiestDaypart) {
    insights.push(
      isRTL
        ? `ذروة الطلبات خلال فترة ${daypartLabel(busiestDaypart.id, isRTL)}.`
        : `Order volume peaks during the ${daypartLabel(busiestDaypart.id, isRTL)} window.`,
    );
  }

  insights.push(
    isRTL
      ? `متوسط وقت التنفيذ الشبكي ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")} وقد ${movement(metrics.fulfilmentMinutesDelta, isRTL)} مقارنة بالفترة السابقة.`
      : `Network-wide fulfilment time averages ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")} and has ${movement(metrics.fulfilmentMinutesDelta, isRTL)} versus the prior period.`,
  );

  return insights.slice(0, 8);
}

function buildMenuInsights(metrics: RestaurantMetrics, fmt: SummaryFormatters): string[] {
  const { isRTL } = fmt;
  const insights: string[] = [];
  const topByRevenue = metrics.menu[0];
  const topByUnits = [...metrics.menu].sort((a, b) => b.units - a.units)[0];
  const topCategory = metrics.categories[0];
  const highestAttach = [...metrics.menu].sort((a, b) => b.attachRate - a.attachRate)[0];
  const slowest = [...metrics.menu].sort((a, b) => a.units - b.units)[0];

  if (topByRevenue) {
    insights.push(
      isRTL
        ? `${menuItemName(topByRevenue.id, isRTL)} يتصدر الإيرادات بـ ${fmt.formatCurrency(topByRevenue.revenue, { compact: true })}.`
        : `${menuItemName(topByRevenue.id, isRTL)} tops revenue at ${fmt.formatCurrency(topByRevenue.revenue, { compact: true })}.`,
    );
  }
  if (topByUnits) {
    const itemsPerOrder = fmt.formatNumber(metrics.itemsPerOrder, 1);
    insights.push(
      isRTL
        ? `${menuItemName(topByUnits.id, isRTL)} الأعلى مبيعاً بالوحدات (${fmt.formatNumber(topByUnits.units)} وحدة)، بمعدل ${itemsPerOrder} عنصر لكل طلب.`
        : `${menuItemName(topByUnits.id, isRTL)} sells the most units (${fmt.formatNumber(topByUnits.units)}), at ${itemsPerOrder} items per order overall.`,
    );
  }
  if (topCategory) {
    insights.push(
      isRTL
        ? `فئة ${categoryLabel(topCategory.id, isRTL)} هي الأعلى إيراداً بين فئات القائمة.`
        : `${categoryLabel(topCategory.id, isRTL)} is the top-earning menu category.`,
    );
  }
  if (highestAttach) {
    insights.push(
      isRTL
        ? `${menuItemName(highestAttach.id, isRTL)} يحقق أعلى معدل إضافة عند ${fmt.formatPercent(highestAttach.attachRate, 0)}.`
        : `${menuItemName(highestAttach.id, isRTL)} has the highest attach rate at ${fmt.formatPercent(highestAttach.attachRate, 0)}.`,
    );
  }
  if (slowest) {
    insights.push(
      isRTL
        ? `${menuItemName(slowest.id, isRTL)} هو الأبطأ حركة بين عناصر القائمة.`
        : `${menuItemName(slowest.id, isRTL)} is the slowest-moving item on the menu.`,
    );
  }

  const totalCategoryRevenue = metrics.categories.reduce((sum, c) => sum + c.revenue, 0);
  const topCategoryShare = topCategory && totalCategoryRevenue > 0 ? topCategory.revenue / totalCategoryRevenue : 0;
  const lowestCategory = [...metrics.categories].sort((a, b) => a.revenue - b.revenue)[0];
  if (topCategory && lowestCategory && topCategory.id !== lowestCategory.id) {
    insights.push(
      isRTL
        ? `تستحوذ فئة ${categoryLabel(topCategory.id, isRTL)} على ${fmt.formatPercent(topCategoryShare)} من إيرادات القائمة، بينما تسجل فئة ${categoryLabel(lowestCategory.id, isRTL)} أدنى مساهمة.`
        : `${categoryLabel(topCategory.id, isRTL)} captures ${fmt.formatPercent(topCategoryShare)} of menu revenue, while ${categoryLabel(lowestCategory.id, isRTL)} contributes the least.`,
    );
  }

  insights.push(
    isRTL
      ? `عدد الوحدات المباعة ${movement(metrics.unitsSoldDelta, isRTL)} مقارنة بالفترة السابقة، بمتوسط ${colorize(fmt.formatNumber(metrics.itemsPerOrder, 1), metrics.itemsPerOrderDelta)} عنصر لكل طلب.`
      : `Units sold have ${movement(metrics.unitsSoldDelta, isRTL)} versus the prior period, averaging ${colorize(fmt.formatNumber(metrics.itemsPerOrder, 1), metrics.itemsPerOrderDelta)} items per order.`,
  );

  const lowestAttach = [...metrics.menu].sort((a, b) => a.attachRate - b.attachRate)[0];
  if (lowestAttach) {
    insights.push(
      isRTL
        ? `${menuItemName(lowestAttach.id, isRTL)} يسجل أدنى معدل إضافة عند ${fmt.formatPercent(lowestAttach.attachRate, 0)}، ما يشير إلى فرصة للترويج المتقاطع.`
        : `${menuItemName(lowestAttach.id, isRTL)} has the lowest attach rate at ${fmt.formatPercent(lowestAttach.attachRate, 0)}, pointing to a cross-sell opportunity.`,
    );
  }

  return insights.slice(0, 9);
}

function buildChannelsCustomersInsights(metrics: RestaurantMetrics, fmt: SummaryFormatters): string[] {
  const { isRTL } = fmt;
  const insights: string[] = [];
  const directIds = new Set(["mobile_app", "website", "pos", "kiosk", "call_center"]);
  const directRevenue = metrics.channels.filter(c => directIds.has(c.id)).reduce((s, c) => s + c.revenue, 0);
  const directShare = metrics.revenue > 0 ? directRevenue / metrics.revenue : 0;
  const topChannel = [...metrics.channels].sort((a, b) => b.share - a.share)[0];

  insights.push(
    isRTL
      ? `القنوات المباشرة تمثل ${fmt.formatPercent(directShare)} من الإيرادات، بقيادة ${topChannel ? channelLabel(topChannel.id, isRTL) : ""}.`
      : `Direct channels account for ${fmt.formatPercent(directShare)} of revenue, led by ${topChannel ? channelLabel(topChannel.id, isRTL) : ""}.`,
  );

  insights.push(
    isRTL
      ? `عدد العملاء ${movement(metrics.customersDelta, isRTL)} إلى ${colorize(fmt.formatNumber(metrics.customers), metrics.customersDelta)}، منهم ${colorize(fmt.formatNumber(metrics.newCustomers), metrics.newCustomersDelta)} عميل جديد.`
      : `Customer count ${movement(metrics.customersDelta, isRTL)} to ${colorize(fmt.formatNumber(metrics.customers), metrics.customersDelta)}, including ${colorize(fmt.formatNumber(metrics.newCustomers), metrics.newCustomersDelta)} new customers.`,
  );

  insights.push(
    isRTL
      ? `معدل تكرار الطلب ${colorize(fmt.formatPercent(metrics.repeatRate), metrics.repeatRateDelta)}.`
      : `Repeat order rate stands at ${colorize(fmt.formatPercent(metrics.repeatRate), metrics.repeatRateDelta)}.`,
  );

  const loyalSegment = metrics.segments.find(s => s.id === "loyal");
  if (loyalSegment) {
    insights.push(
      isRTL
        ? `شريحة العملاء الأوفياء تضم ${fmt.formatNumber(loyalSegment.customers)} عميل بمتوسط ${fmt.formatNumber(loyalSegment.ordersPerCustomer, 1)} طلب لكل عميل.`
        : `The loyal segment includes ${fmt.formatNumber(loyalSegment.customers)} customers averaging ${fmt.formatNumber(loyalSegment.ordersPerCustomer, 1)} orders each.`,
    );
  }

  const remainingOpportunity = metrics.cart.lostRevenue - metrics.cart.recoveredRevenue;
  insights.push(
    isRTL
      ? `فرصة استرداد سلة إضافية بقيمة ${fmt.formatCurrency(Math.max(remainingOpportunity, 0), { compact: true })} لا تزال متاحة.`
      : `${fmt.formatCurrency(Math.max(remainingOpportunity, 0), { compact: true })} of abandoned-cart recovery opportunity remains untapped.`,
  );

  const bestChannelAov = [...metrics.channels].sort((a, b) => b.aov - a.aov)[0];
  if (bestChannelAov) {
    insights.push(
      isRTL
        ? `${channelLabel(bestChannelAov.id, isRTL)} يحقق أعلى متوسط لقيمة الطلب بين القنوات عند ${fmt.formatCurrencyAverage(bestChannelAov.aov)}.`
        : `${channelLabel(bestChannelAov.id, isRTL)} delivers the highest average order value among channels, at ${fmt.formatCurrencyAverage(bestChannelAov.aov)}.`,
    );
  }

  const atRiskSegment = metrics.segments.find(s => s.id === "at_risk");
  if (atRiskSegment) {
    insights.push(
      isRTL
        ? `شريحة العملاء المعرضين للتسرب تضم ${fmt.formatNumber(atRiskSegment.customers)} عميل بمتوسط ${fmt.formatNumber(atRiskSegment.ordersPerCustomer, 1)} طلب لكل عميل، ما يستدعي إعادة تفعيل.`
        : `The at-risk segment includes ${fmt.formatNumber(atRiskSegment.customers)} customers averaging ${fmt.formatNumber(atRiskSegment.ordersPerCustomer, 1)} orders each, warranting a win-back push.`,
    );
  }

  const latestCohort = metrics.retention[metrics.retention.length - 1];
  const earlyCohort = metrics.retention.find(c => c.retention.length >= 3);
  if (earlyCohort && earlyCohort.retention[1] !== undefined) {
    insights.push(
      isRTL
        ? `مجموعة عملاء ${earlyCohort.cohort} تحتفظ بـ ${fmt.formatPercent(earlyCohort.retention[1])} من عملائها بعد الشهر الأول.`
        : `The ${earlyCohort.cohort} customer cohort retains ${fmt.formatPercent(earlyCohort.retention[1])} of customers into month one.`,
    );
  } else if (latestCohort) {
    insights.push(
      isRTL
        ? `أحدث مجموعة عملاء (${latestCohort.cohort}) تضم ${fmt.formatNumber(latestCohort.size)} عميل جديد.`
        : `The most recent customer cohort (${latestCohort.cohort}) includes ${fmt.formatNumber(latestCohort.size)} new customers.`,
    );
  }

  insights.push(
    isRTL
      ? `معدل استرداد سلات التسوق المهجورة ${fmt.formatPercent(metrics.cart.recoveryRate)}، من إجمالي معدل هجر ${fmt.formatPercent(metrics.cart.abandonRate)}.`
      : `Abandoned-cart recovery rate stands at ${fmt.formatPercent(metrics.cart.recoveryRate)}, against an overall abandonment rate of ${fmt.formatPercent(metrics.cart.abandonRate)}.`,
  );

  return insights.slice(0, 9);
}

function buildLeadSummary(
  tab: RestaurantSummaryTab,
  metrics: RestaurantMetrics,
  filters: RestaurantFilters,
  fmt: SummaryFormatters,
): string {
  const { isRTL } = fmt;

  if (tab === "overview") {
    const topType = [...metrics.orderTypes].sort((a, b) => b.share - a.share)[0];
    const busiestDaypart = [...metrics.dayparts].sort((a, b) => b.orders - a.orders)[0];
    return isRTL
      ? `بلغت الإيرادات ${colorize(fmt.formatCurrency(metrics.revenue, { compact: true }), metrics.revenueDelta)} عبر ${colorize(fmt.formatNumber(metrics.orders), metrics.ordersDelta)} طلب، حيث ${movement(metrics.revenueDelta, isRTL)} مقارنة بالفترة السابقة. يقود ${topType ? orderTypeLabel(topType.id, isRTL) : "مزيج الطلبات"} حركة الطلب بينما بلغ متوسط قيمة الطلب ${colorize(fmt.formatCurrencyAverage(metrics.aov), metrics.aovDelta)}، ومعدل تكرار الطلب ${colorize(fmt.formatPercent(metrics.repeatRate), metrics.repeatRateDelta)}. ${busiestDaypart ? `فترة ${daypartLabel(busiestDaypart.id, isRTL)} هي الأكثر ازدحاماً، ` : ""}بمتوسط وقت تنفيذ ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")} عبر ${fmt.formatNumber(metrics.activeBranches)} فرع نشط.`
      : `Revenue reached ${colorize(fmt.formatCurrency(metrics.revenue, { compact: true }), metrics.revenueDelta)} across ${colorize(fmt.formatNumber(metrics.orders), metrics.ordersDelta)} orders, having ${movement(metrics.revenueDelta, isRTL)} versus the prior period. ${topType ? orderTypeLabel(topType.id, isRTL) : "Order mix"} leads demand, with average order value at ${colorize(fmt.formatCurrencyAverage(metrics.aov), metrics.aovDelta)} and a ${colorize(fmt.formatPercent(metrics.repeatRate), metrics.repeatRateDelta)} repeat order rate. ${busiestDaypart ? `${daypartLabel(busiestDaypart.id, isRTL)} is the busiest daypart, with ` : "The network averages "}${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")} average fulfilment across ${fmt.formatNumber(metrics.activeBranches)} active branches.`;
  }

  if (tab === "sales-orders") {
    const cancelRate = metrics.orders > 0 ? metrics.cancelledOrders / metrics.orders : 0;
    const discountShare = metrics.revenue > 0 ? metrics.discountAmount / metrics.revenue : 0;
    const strongestDay = [...metrics.daily].sort((a, b) => b.revenue - a.revenue)[0];
    return isRTL
      ? `حققت الإيرادات ${colorize(fmt.formatCurrency(metrics.revenue, { compact: true }), metrics.revenueDelta)} خلال الفترة المحددة بمعدل إلغاء ${colorize(fmt.formatPercent(cancelRate), metrics.cancelledOrdersDelta, "inverse")}. عدد الطلبات ${movement(metrics.ordersDelta, isRTL)} إلى ${colorize(fmt.formatNumber(metrics.orders), metrics.ordersDelta)} طلب بمتوسط ${colorize(fmt.formatNumber(metrics.itemsPerOrder, 1), metrics.itemsPerOrderDelta)} عنصر لكل طلب. تمثل الخصومات ${colorize(fmt.formatPercent(discountShare), metrics.discountAmountDelta, "inverse")} من الإيرادات${strongestDay ? `، وكان ${fmt.formatDate(strongestDay.date)} أقوى يوم بإيرادات ${fmt.formatCurrency(strongestDay.revenue, { compact: true })}` : ""}.`
      : `Revenue totalled ${colorize(fmt.formatCurrency(metrics.revenue, { compact: true }), metrics.revenueDelta)} for the selected period, with a ${colorize(fmt.formatPercent(cancelRate), metrics.cancelledOrdersDelta, "inverse")} cancellation rate. Order volume ${movement(metrics.ordersDelta, isRTL)} to ${colorize(fmt.formatNumber(metrics.orders), metrics.ordersDelta)} orders, averaging ${colorize(fmt.formatNumber(metrics.itemsPerOrder, 1), metrics.itemsPerOrderDelta)} items per order. Discounts account for ${colorize(fmt.formatPercent(discountShare), metrics.discountAmountDelta, "inverse")} of revenue${strongestDay ? `, and ${fmt.formatDate(strongestDay.date)} was the strongest day at ${fmt.formatCurrency(strongestDay.revenue, { compact: true })}` : ""}.`;
  }

  if (tab === "branches") {
    const branchCancelRate = metrics.orders > 0 ? metrics.cancelledOrders / metrics.orders : 0;
    if (filters.branchId !== "all" && metrics.branches.length === 1) {
      const branch = metrics.branches[0];
      return isRTL
        ? `${branchName(branch.id, isRTL)} حقق إيرادات ${fmt.formatCurrency(branch.revenue, { compact: true })} من ${fmt.formatNumber(branch.orders)} طلب، بمتوسط قيمة طلب ${fmt.formatCurrencyAverage(branch.aov)}. عدد الطلبات ${movement(metrics.ordersDelta, isRTL)} مقارنة بالفترة السابقة، بمعدل إلغاء ${colorize(fmt.formatPercent(branchCancelRate), metrics.cancelledOrdersDelta, "inverse")}. متوسط وقت التنفيذ ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")} وقد ${movement(metrics.fulfilmentMinutesDelta, isRTL)}.`
        : `${branchName(branch.id, isRTL)} generated ${fmt.formatCurrency(branch.revenue, { compact: true })} from ${fmt.formatNumber(branch.orders)} orders, at an average order value of ${fmt.formatCurrencyAverage(branch.aov)}. Order volume ${movement(metrics.ordersDelta, isRTL)} versus the prior period, with a ${colorize(fmt.formatPercent(branchCancelRate), metrics.cancelledOrdersDelta, "inverse")} cancellation rate. Average fulfilment time is ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")} and has ${movement(metrics.fulfilmentMinutesDelta, isRTL)}.`;
    }
    const leader = metrics.branches[0];
    const weakest = metrics.branches[metrics.branches.length - 1];
    const top3Share = metrics.branches.slice(0, 3).reduce((sum, b) => sum + b.share, 0);
    return isRTL
      ? `${leader ? branchName(leader.id, isRTL) : "الفرع الأعلى أداءً"} يقود الشبكة${leader ? ` بإيرادات ${fmt.formatCurrency(leader.revenue, { compact: true })}` : ""} بينما تمثل أفضل 3 فروع ${fmt.formatPercent(top3Share)} من إجمالي الإيرادات. متوسط وقت التنفيذ الشبكي ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")} وقد ${movement(metrics.fulfilmentMinutesDelta, isRTL)} مقارنة بالفترة السابقة. ${weakest && leader && weakest.id !== leader.id ? `${branchName(weakest.id, isRTL)} يسجل أدنى أداء ويحتاج إلى اهتمام، ` : ""}عبر ${fmt.formatNumber(metrics.activeBranches)} فرع نشط.`
      : `${leader ? branchName(leader.id, isRTL) : "The top-performing branch"} leads the network${leader ? ` with ${fmt.formatCurrency(leader.revenue, { compact: true })} in revenue` : ""}, and the top 3 branches account for ${fmt.formatPercent(top3Share)} of total revenue. Network-wide fulfilment time averages ${colorize(fmt.formatMinutes(metrics.fulfilmentMinutes), metrics.fulfilmentMinutesDelta, "inverse")} and has ${movement(metrics.fulfilmentMinutesDelta, isRTL)} versus the prior period. ${weakest && leader && weakest.id !== leader.id ? `${branchName(weakest.id, isRTL)} trails the network and needs attention, across ` : "Across "}${fmt.formatNumber(metrics.activeBranches)} active branches.`;
  }

  if (tab === "menu") {
    const topByRevenue = metrics.menu[0];
    const topByUnits = [...metrics.menu].sort((a, b) => b.units - a.units)[0];
    const topCategory = metrics.categories[0];
    const slowest = [...metrics.menu].sort((a, b) => a.units - b.units)[0];
    return isRTL
      ? `${topByRevenue ? menuItemName(topByRevenue.id, isRTL) : "أفضل عنصر"} يتصدر الإيرادات بـ ${topByRevenue ? fmt.formatCurrency(topByRevenue.revenue, { compact: true }) : ""}، وتقود فئة ${topCategory ? categoryLabel(topCategory.id, isRTL) : ""} أداء الفئات. عدد الوحدات المباعة ${movement(metrics.unitsSoldDelta, isRTL)} مقارنة بالفترة السابقة، بمعدل ${colorize(fmt.formatNumber(metrics.itemsPerOrder, 1), metrics.itemsPerOrderDelta)} عنصر لكل طلب.${topByUnits ? ` ${menuItemName(topByUnits.id, isRTL)} الأعلى مبيعاً بالوحدات` : ""}${slowest && topByUnits && slowest.id !== topByUnits.id ? `، بينما ${menuItemName(slowest.id, isRTL)} هو الأبطأ حركة` : ""}.`
      : `${topByRevenue ? menuItemName(topByRevenue.id, isRTL) : "The top item"} leads revenue at ${topByRevenue ? fmt.formatCurrency(topByRevenue.revenue, { compact: true }) : ""}, with ${topCategory ? categoryLabel(topCategory.id, isRTL) : "the top category"} leading category performance. Units sold have ${movement(metrics.unitsSoldDelta, isRTL)} versus the prior period, averaging ${colorize(fmt.formatNumber(metrics.itemsPerOrder, 1), metrics.itemsPerOrderDelta)} items per order.${topByUnits ? ` ${menuItemName(topByUnits.id, isRTL)} sells the most units` : ""}${slowest && topByUnits && slowest.id !== topByUnits.id ? `, while ${menuItemName(slowest.id, isRTL)} is the slowest mover` : ""}.`;
  }

  const directIds = new Set(["mobile_app", "website", "pos", "kiosk", "call_center"]);
  const directRevenue = metrics.channels.filter(c => directIds.has(c.id)).reduce((s, c) => s + c.revenue, 0);
  const directShare = metrics.revenue > 0 ? directRevenue / metrics.revenue : 0;
  const topChannel = [...metrics.channels].sort((a, b) => b.share - a.share)[0];
  const loyalSegment = metrics.segments.find(s => s.id === "loyal");
  return isRTL
    ? `القنوات المباشرة تمثل ${fmt.formatPercent(directShare)} من الإيرادات${topChannel ? `، بقيادة ${channelLabel(topChannel.id, isRTL)}` : ""}. عدد العملاء ${movement(metrics.customersDelta, isRTL)} إلى ${colorize(fmt.formatNumber(metrics.customers), metrics.customersDelta)}، منهم ${colorize(fmt.formatNumber(metrics.newCustomers), metrics.newCustomersDelta)} عميل جديد، بمعدل تكرار طلب ${colorize(fmt.formatPercent(metrics.repeatRate), metrics.repeatRateDelta)}.${loyalSegment ? ` تضم شريحة العملاء الأوفياء ${fmt.formatNumber(loyalSegment.customers)} عميل بمتوسط ${fmt.formatNumber(loyalSegment.ordersPerCustomer, 1)} طلب لكل عميل.` : ""}`
    : `Direct channels account for ${fmt.formatPercent(directShare)} of revenue${topChannel ? `, led by ${channelLabel(topChannel.id, isRTL)}` : ""}. Customer count ${movement(metrics.customersDelta, isRTL)} to ${colorize(fmt.formatNumber(metrics.customers), metrics.customersDelta)}, including ${colorize(fmt.formatNumber(metrics.newCustomers), metrics.newCustomersDelta)} new customers, with a ${colorize(fmt.formatPercent(metrics.repeatRate), metrics.repeatRateDelta)} repeat order rate.${loyalSegment ? ` The loyal segment holds ${fmt.formatNumber(loyalSegment.customers)} customers averaging ${fmt.formatNumber(loyalSegment.ordersPerCustomer, 1)} orders each.` : ""}`;
}

function buildActions(tab: RestaurantSummaryTab, metrics: RestaurantMetrics, fmt: SummaryFormatters): string[] {
  const { isRTL } = fmt;

  if (tab === "overview") {
    const busiestDaypart = [...metrics.dayparts].sort((a, b) => b.orders - a.orders)[0];
    const actions = [
      isRTL
        ? `ركّز الترويج والتوظيف على فترة ${busiestDaypart ? daypartLabel(busiestDaypart.id, isRTL) : ""} خلال عطلة نهاية الأسبوع.`
        : `Concentrate promotions and staffing on the ${busiestDaypart ? daypartLabel(busiestDaypart.id, isRTL) : ""} weekend peak.`,
    ];
    if (metrics.branches.length > 1) {
      const weakest = metrics.branches[metrics.branches.length - 1];
      actions.push(
        isRTL
          ? `راجع تشغيل ${branchName(weakest.id, isRTL)} لتقليص الفجوة مع أفضل أداء في الشبكة.`
          : `Review operations at ${branchName(weakest.id, isRTL)} to close the gap with the network's best performers.`,
      );
    }
    return actions;
  }

  if (tab === "sales-orders") {
    const actions: string[] = [];
    const discountShare = metrics.revenue > 0 ? metrics.discountAmount / metrics.revenue : 0;
    if (discountShare > 0.045) {
      actions.push(
        isRTL
          ? "شدّد شروط العروض الترويجية للحفاظ على هامش الربح."
          : "Tighten promotional offer terms to protect margin.",
      );
    } else {
      const bestAov = [...metrics.orderTypes].sort((a, b) => b.aov - a.aov)[0];
      actions.push(
        isRTL
          ? `وجّه الطلب نحو ${bestAov ? orderTypeLabel(bestAov.id, isRTL) : "أنواع الطلب"} صاحب أعلى متوسط قيمة طلب.`
          : `Steer demand toward ${bestAov ? orderTypeLabel(bestAov.id, isRTL) : "the order type"} with the best average order value.`,
      );
    }
    const cancelRate = metrics.orders > 0 ? metrics.cancelledOrders / metrics.orders : 0;
    if (cancelRate > 0.02 || metrics.refundedAmountDelta > 0) {
      actions.push(
        isRTL
          ? "افحص أسباب الإلغاء والاسترداد لخفض الفاقد التشغيلي."
          : "Investigate cancellation and refund drivers to reduce operational leakage.",
      );
    }
    return actions;
  }

  if (tab === "branches") {
    const actions: string[] = [];
    if (metrics.branches.length > 1) {
      const worst = metrics.branches[metrics.branches.length - 1];
      const leader = metrics.branches[0];
      actions.push(
        isRTL
          ? `طبّق ممارسات ${branchName(leader.id, isRTL)} الناجحة على ${branchName(worst.id, isRTL)} لرفع الأداء.`
          : `Apply ${branchName(leader.id, isRTL)}'s playbook at ${branchName(worst.id, isRTL)} to lift performance.`,
      );
    }
    if (metrics.fulfilmentMinutesDelta > 2) {
      actions.push(
        isRTL
          ? "راجع سعة المطبخ وجدولة الموظفين لتقليص وقت التنفيذ."
          : "Review kitchen capacity and staff scheduling to bring fulfilment time back down.",
      );
    }
    return actions;
  }

  if (tab === "menu") {
    const topItem = metrics.menu[0];
    const highestAttach = [...metrics.menu].sort((a, b) => b.attachRate - a.attachRate)[0];
    const slowest = [...metrics.menu].sort((a, b) => a.units - b.units)[0];
    const actions = [
      isRTL
        ? `اجمع ${highestAttach ? menuItemName(highestAttach.id, isRTL) : ""} مع ${topItem ? menuItemName(topItem.id, isRTL) : ""} في عرض مجمّع.`
        : `Bundle ${highestAttach ? menuItemName(highestAttach.id, isRTL) : "the high-attach item"} with ${topItem ? menuItemName(topItem.id, isRTL) : "the top seller"}.`,
    ];
    if (slowest) {
      actions.push(
        isRTL
          ? `أعد النظر في تسعير أو عرض ${menuItemName(slowest.id, isRTL)}، أو استبدله بعنصر أعلى دورانا.`
          : `Reconsider the pricing or placement of ${menuItemName(slowest.id, isRTL)}, or rotate it out for a higher-turnover item.`,
      );
    }
    return actions;
  }

  if (tab === "channels-customers") {
    const actions = [
      isRTL
        ? "أطلق حملة استرداد عبر واتساب وحوّل الطلب نحو القنوات المباشرة."
        : "Launch a WhatsApp recovery push and shift demand toward direct channels.",
    ];
    const atRiskSegment = metrics.segments.find(s => s.id === "at_risk");
    if (atRiskSegment && atRiskSegment.customers > 0) {
      actions.push(
        isRTL
          ? "شغّل حملة إعادة تفعيل مستهدفة لشريحة العملاء المعرضين للتسرب."
          : "Run a targeted win-back campaign for the at-risk customer segment.",
      );
    }
    return actions;
  }

  return [];
}

function formatPeriodLabel(startIso: string, endIso: string, isRTL: boolean): string {
  const start = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  const formatter = new Intl.DateTimeFormat(isRTL ? "ar-EG-u-nu-arab" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${formatter.format(start)} – ${formatter.format(end)}`;
}

export interface RestaurantExecutiveSummary extends DashboardNarrativeContent {
  hasData: boolean;
  periodLabel: string;
  action: string | null;
}

export function generateRestaurantExecutiveSummary({
  tab,
  metrics,
  filters,
  fmt,
}: BuildSummaryParams): RestaurantExecutiveSummary {
  const hasData = metrics.orders > 0;
  const periodLabel = formatPeriodLabel(filters.startDate, filters.endDate, fmt.isRTL);

  if (!hasData) {
    return {
      summary: "",
      keyInsights: [],
      recommendations: [],
      hasData: false,
      periodLabel,
      action: null,
    };
  }

  let insights: string[];
  if (tab === "overview") {insights = buildOverviewInsights(metrics, filters, fmt);}
  else if (tab === "sales-orders") {insights = buildSalesOrdersInsights(metrics, fmt);}
  else if (tab === "branches") {insights = buildBranchesInsights(metrics, filters, fmt);}
  else if (tab === "menu") {insights = buildMenuInsights(metrics, fmt);}
  else {insights = buildChannelsCustomersInsights(metrics, fmt);}

  const leadSummary = buildLeadSummary(tab, metrics, filters, fmt);

  const lowData = metrics.orders < 900 || metrics.daily.length < 7;
  const summarySegments: DashboardNarrativeSegment[] = lowData
    ? [{ text: fmt.isRTL ? "بيانات محدودة لهذه الفترة — " : "Limited data for this period — ", type: "text" }]
    : [];

  const actions = buildActions(tab, metrics, fmt);
  const action = actions[0] ?? null;

  return {
    summary: leadSummary,
    summarySegments: summarySegments.length ? [...summarySegments, { text: leadSummary, type: "text" }] : undefined,
    keyInsights: insights,
    recommendations: actions,
    hasData: true,
    periodLabel,
    action,
  };
}
